import { generateId } from "./generate-id";
import { envConstants } from "../../constant/env-constant";

type LogLevel = "info" | "warn" | "error" | "debug";

const LEVEL_RANK: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3
};

/**
 * Level gate. Checked BEFORE formatMessage so suppressed levels pay neither
 * the JSON.stringify nor the console/stdout cost. Default level is info;
 * hot-path per-request logs use debug and are skipped unless
 * UF_LOG_LEVEL=debug is set (see env-constant.ts LOG_LEVEL).
 * The level is resolved once lazily — UF_LOG_LEVEL is static per deployment,
 * and reading process.env on every (suppressed) log call is avoidable cost.
 */
let resolvedLevelRank: number | null = null;
function shouldLog(level: LogLevel): boolean {
  if (resolvedLevelRank === null) {
    const configured = envConstants.LOG_LEVEL as LogLevel;
    resolvedLevelRank = LEVEL_RANK[configured] ?? LEVEL_RANK.info;
  }
  return LEVEL_RANK[level] <= resolvedLevelRank;
}

function formatMessage(
  level: LogLevel,
  module: string,
  message: string,
  meta?: Record<string, any>
) {
  const buildNumber = envConstants.BUILD_VERSION;
  const prefix = `[${level.toUpperCase()}] [${module}-${buildNumber}]`;
  const metaStr = meta ? ` ${JSON.stringify(meta)}` : "";
  return `${prefix} ${message}${metaStr}`;
}
export function getLogger(module: string) {
  return {
    info: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("info")) {
        console.info(formatMessage("info", module, message, meta));
      }
    },
    warn: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("warn")) {
        console.warn(formatMessage("warn", module, message, meta));
      }
    },
    error: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("error")) {
        console.error(formatMessage("error", module, message, meta));
      }
    },
    debug: (message: string, meta?: Record<string, any>) => {
      if (shouldLog("debug")) {
        console.debug(formatMessage("debug", module, message, meta));
      }
    }
  };
}

/* -------------------------------------------------------

   Page Request Logger → Storyblok pages

------------------------------------------------------- */

export function getPageSessionId() {
  return generateId();
}

export function logPageRequest(
  path: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`PageTracker:${path}`);

  logger.info(message, {
    path,
    ...(meta || {})
  });
}

/**
 * Debug-level twin of logPageRequest for per-request verbose logs.
 * Same logger name and shape — set UF_LOG_LEVEL=debug to re-enable.
 */
export function logPageRequestDebug(
  path: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`PageTracker:${path}`);

  logger.debug(message, {
    path,
    ...(meta || {})
  });
}

/* -------------------------------------------------------

   Service Logger → for business/infra

------------------------------------------------------- */

export function logServiceEvent(
  serviceName: string,
  message: string,
  meta?: Record<string, any>
) {
  const updatedServiceName = `${
    meta?.prefix ? `${meta?.prefix}-` : ""
  }${serviceName}${meta?.suffix ? `-${meta?.suffix}` : ""}`;
  const logger = getLogger(`Service:${updatedServiceName}`);
  logger.info(message, meta);
}

/* -------------------------------------------------------

   Orchestration Logger → for workflows

------------------------------------------------------- */

export function logOrchestrationEvent(
  orchestrationName: string,
  message: string,
  meta?: Record<string, any>
) {
  const updatedOrchestrationName = `${
    meta?.prefix ? `${meta?.prefix}-` : ""
  }${orchestrationName}${meta?.suffix ? `-${meta?.suffix}` : ""}`;
  const logger = getLogger(`Orchestration:${updatedOrchestrationName}`);
  logger.info(message, meta);
}

/* -------------------------------------------------------

   Error Logger → generic + page errors

------------------------------------------------------- */

export function logError(
  scope: string, // e.g. "StoryblokService" or "CacheWarmup"
  error: unknown,
  meta?: Record<string, any>
) {
  const updateScope = `${meta?.prefix ? `${meta?.prefix}-` : ""}${scope}${
    meta?.suffix ? `-${meta?.suffix}` : ""
  }`;
  const logger = getLogger(`Error:${updateScope}`);
  if (error instanceof Error) {
    logger.error("Error occurred", {
      err: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      ...(meta || {})
    });
  } else {
    logger.error("Error occurred", {
      err: error,
      ...(meta || {})
    });
  }
}

export function logPageError(
  scope: string, // e.g. "getResolvedPageComponents" or "PageRenderer"
  error: unknown,
  meta?: Record<string, any>
) {
  const logger = getLogger("PageTracker");
  if (error instanceof Error) {
    logger.error("Page error", {
      err: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      scope,
      ...(meta || {})
    });
  } else {
    logger.error("Page error", {
      err: error,
      ...(meta || {})
    });
  }
}

export function logPageInfo(
  scope: string, // e.g. "getResolvedPageComponents" or "PageRenderer"
  error: unknown,
  meta?: Record<string, any>
) {
  const logger = getLogger("PageTracker");
  if (error instanceof Error) {
    logger.info("Page info", {
      err: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      scope,
      ...(meta || {})
    });
  } else {
    logger.info("Page info", {
      err: error,
      ...(meta || {})
    });
  }
}

export function logCornJobEvent(
  jobName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`CronJob:${jobName}`);
  logger.info(message, meta);
}

export function logAdapterEvent(
  adapterName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`Adapter:${adapterName}`);
  logger.info(message, meta);
}

export function logGenericEvent(
  loggerName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(loggerName);
  logger.info(message, meta);
}

/**
 * Debug-level twin of logGenericEvent for per-request verbose logs.
 * Same logger name and shape — set UF_LOG_LEVEL=debug to re-enable.
 */
export function logGenericDebug(
  loggerName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(loggerName);
  logger.debug(message, meta);
}

/**
 * Debug-level twin of logServiceEvent for per-request verbose logs.
 */
export function logServiceDebug(
  serviceName: string,
  message: string,
  meta?: Record<string, any>
) {
  const updatedServiceName = `${
    meta?.prefix ? `${meta?.prefix}-` : ""
  }${serviceName}${meta?.suffix ? `-${meta?.suffix}` : ""}`;
  const logger = getLogger(`Service:${updatedServiceName}`);
  logger.debug(message, meta);
}

/**
 * Debug-level twin of logAdapterEvent for per-request verbose logs.
 */
export function logAdapterDebug(
  adapterName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`Adapter:${adapterName}`);
  logger.debug(message, meta);
}

export function logRouteEvent(
  routeName: string,
  message: string,
  meta?: Record<string, any>
) {
  const logger = getLogger(`Route:${routeName}`);
  logger.info(message, meta);
}
